import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, FormGroup, Tooltip, Position, Icon } from "@blueprintjs/core";
import Avatar from 'react-avatar';
import "./ReviewStepper.css";
import AddUser from "../../assets/AddUser.svg";
import ScrollableDropdown from '../shared/ScrollableDropdown';
import { FilledButton } from '../buttons-20250224T111359Z-001/buttons';

export default function ReviewStepper({
  steps = [],
  activeStepId,
  employees = [],
  selectedEmployee,
  onAddStep,
  onUpdateStep,
  onDeleteStep,
  onSelectStep,
  canModify = false,
  ratingMethod = 0, // 0=last,1=avg,2=by weightage
}) {
  const MAX_WORDS = 8;
  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [selectedReviewer, setSelectedReviewer] = useState(null);

  const stepperRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const [editData, setEditData] = useState({
    id: "",
    stepName: "",
    reviewerId: "",
    reviewerName: "",
    weightage: ""
  });
  const [errors, setErrors] = useState({
    stepName: "",
    reviewer: "",
    weightage: ""
  });



  const selectedStepJobProfileId = useMemo(() => {
    const step = steps.find(s => s.id === editData.id);
    return step?.job_profile_id || null;
  }, [steps, editData.id]);


  const filteredEmployeeList = useMemo(() => {
    if (!selectedStepJobProfileId) return employees;
    return employees.filter(
      emp => emp.job_profile_id === selectedStepJobProfileId
    );
  }, [employees, selectedStepJobProfileId]);

  // keep errors cleared when drawer closes
  const closeDrawer = () => {
    setOpen(false);
    setEditIndex(null);
    setErrors({ stepName: "", reviewer: "", weightage: "" });
    setEditData({ id: "", stepName: "", reviewerId: "", reviewerName: "", weightage: "" });
    setSelectedReviewer(null);
  };

  const updateStepField = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    let valid = true;
    let newErrors = { stepName: "", reviewer: "", weightage: "" };

    if (!editData.stepName?.trim()) {
      newErrors.stepName = "Step name cannot be empty.";
      valid = false;
    }

    if (!editData.reviewerId) {
      newErrors.reviewer = "Please select a reviewer.";
      valid = false;
    }

    if (editData.reviewerId && selectedEmployee && editData.reviewerId === selectedEmployee.id) {
      newErrors.reviewer = "The selected employee cannot be assigned as their own reviewer.";
      valid = false;
    }

    if (editData.reviewerId) {
      const reviewerAlreadyUsed = steps.some((s, idx) => {
        return s.assigned_user_id === editData.reviewerId && idx !== editIndex;
      });
      if (reviewerAlreadyUsed) {
        newErrors.reviewer = "This reviewer is already assigned to another step.";
        valid = false;
      }
    }

    // If ratingMethod === 2 require weightage and ensure total sum === 100
    // if (Number(ratingMethod) === 2) {
    //   const w = editData.weightage === "" ? null : Number(editData.weightage);
    //   if (w === null || Number.isNaN(w)) {
    //     newErrors.weightage = "Weightage is required when using weighted rating.";
    //     valid = false;
    //   } else if (w < 0 || w > 100) {
    //     newErrors.weightage        valid = false;
    //     = "Weightage must be between 0 and 100.";
    //   valid = false;
    // }


    if (Number(ratingMethod) === 2) {
      const w = editData.weightage === "" ? null : Number(editData.weightage);
      if (w === null || Number.isNaN(w)) {
        newErrors.weightage = "Weightage is required when using weighted rating.";
        valid = false;
      } else if (w < 0 || w > 100) {
        newErrors.weightage = "Weightage must be between 0 and 100.";
        valid = false;
      }
    }
    setErrors(newErrors);
    return valid;
  };

  const openAddDrawer = () => {
    if (!canModify) return;
    setEditIndex(null);
    setEditData({ id: "", stepName: "", reviewerId: "", reviewerName: "", weightage: "" });
    setSelectedReviewer(null);
    setErrors({ stepName: "", reviewer: "", weightage: "" });
    setOpen(true);
  };

  const openEditDrawer = (i) => {
    if (!canModify) return;
    const step = steps[i] || {};
    const reviewer = employees.find(e => e.id === step.assigned_user_id) || null;
    setEditIndex(i);
    setEditData({
      id: step.id || "",
      stepName: step.level_name || "",
      jobProfileId: step?.job_profile_id || "",
      reviewerId: step.assigned_user_id || "",
      reviewerName: step.assigned_user_name || "",
      weightage: step.weightage !== undefined ? String(step.weightage) : ""
    });
    setSelectedReviewer(reviewer);
    setErrors({ stepName: "", reviewer: "", weightage: "" });
    setOpen(true);
  };

  useEffect(() => {
  }, [open]);

  const saveStep = () => {
    if (!validateForm()) return;

    const payload = {
      level_name: editData.stepName,
      assigned_user_id: editData.reviewerId,
      // only send weightage when ratingMethod === 2
      ...(Number(ratingMethod) === 2 ? { weightage: Number(editData.weightage || 0) } : {})
    };

    if (editIndex === null) {
      onAddStep && onAddStep(payload);
    } else {
      payload.id = editData.id;
      onUpdateStep && onUpdateStep(payload);
    }
    closeDrawer();
  };

  const deleteStep = () => {
    if (editIndex !== null) {
      const payload = { id: editData.id, assigned_user_id: editData.reviewerId };
      onDeleteStep && onDeleteStep(payload);
    }
    closeDrawer();
  };

  const handleSelectReviewer = (employee) => {
    setSelectedReviewer(employee);
    setEditData(prev => ({ ...prev, reviewerId: employee.id, reviewerName: `${employee.first_name || ""} ${employee.last_name || ""}` }));
    if (errors.reviewer) setErrors(prev => ({ ...prev, reviewer: "" }));
  };

  const handleSearchEmployees = () => { };

  const updateScrollButtons = () => {
    const el = stepperRef.current;
    if (!el) return;

    const { scrollLeft, clientWidth, scrollWidth } = el;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };


  useEffect(() => {
    const el = stepperRef.current;
    if (!el) return;

    updateScrollButtons();

    el.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [steps?.length]);

  const scrollByAmount = (dir) => {
    const el = stepperRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 150, behavior: 'smooth' });
  };

  // render
  return (
    <>
      <div
        className={`review-stepper-wrapper ${!canModify ? "review-stepper-wrapper--readonly" : ""
          }`}
      >
        {canScrollLeft && (
          <button
            className="stepper-arrow stepper-arrow-left"
            onClick={() => scrollByAmount(-1)}
            aria-label="Scroll left"
          >
            <Icon icon="chevron-left" />
          </button>
        )}
        <div className="review-stepper" ref={stepperRef}>
          <div className="stepper-inner">
            {steps?.map((step, i) => {
              // const status = step?.isCompleted
              //   ? "completed"
              //   : step.isActive
              //     ? "active"
              //     : "pending";
              const isActiveStep = step.id === activeStepId;

              const status = step?.isCompleted
                ? "completed"
                : step?.isActive
                  ? "active"
                  : "pending";

              const imageUrl = step.assigned_user_image || I18n.t('js.pg_avatar_standard');
              const isLast = i === (steps?.length || 0) - 1;
              
              return (
                <div key={i} className={`step-item ${isActiveStep ? "active-step-highlight-box" : ""}`}>


                  <div className="step-text push-10-t" style={{ cursor: "pointer" }}>
                    <Tooltip 
                      content={step.assigned_user_name || "Unassigned"}
                      position={Position.TOP}
                      portalContainer={document.body}
                      modifiers={{
                        flip: { enabled: false },
                        preventOverflow: { enabled: false }
                      }}
                    >
                      <div
                        className={`step-circle ${status} ${isActiveStep ? "active-step-highlight" : ""}`}
                        onClick={() => onSelectStep(step)}
                        title={step.assigned_user_name || "Unassigned"}
                      >
                        <Avatar
                          className="step-avatar"
                          src={imageUrl}
                          name={step.assigned_user_name || ""}
                          round={true}
                        />
                      </div>
                    </Tooltip>

                    {!(!canModify && isLast) && (
                      <div className={`step-line ${status}`} />
                    )}

                    <div className="step-text push-10-t" onClick={() => canModify && openEditDrawer(i)}>
                      <div className="step-user-info">
                        <Tooltip 
                          content={step.level_name}
                          position={Position.TOP}
                          portalContainer={document.body}
                        >
                          <p className="step-user-name" style={{ cursor: "default" }}>
                            {(step?.job_profile_id && !step?.assigned_user_id)
                              ? `Select ${step?.job_profile_name}`
                              : step?.level_name
                            }
                          </p>
                        </Tooltip>
                      </div>

                    </div>
                  </div>

                </div>
              );
            })}
          </div>

       

          {canModify && (<div className="step-item add-step-section" onClick={openAddDrawer}>
            <div className="step-text push-10-t">
              <div className="add-step-circle">
                <img src={AddUser} alt="Add Step" className="add-step-icon" />
              </div>
              <div className="step-text push-10-t">
                <p className="add-step-text" style={{ color: "#2d8cff" }}>Add Step</p>
              </div>
            </div>
          </div>)}
        </div>
        {canScrollRight && (
          <button
            className="stepper-arrow stepper-arrow-right"
            onClick={() => scrollByAmount(1)}
            aria-label="Scroll right"
          >
            <Icon icon="chevron-right" />
          </button>
        )}
      </div>

      {/* DRAWER */}
      <Dialog
        isOpen={open}
        onClose={closeDrawer}
        title={editIndex === null ? "Add New Review Step" : "Edit Review Step"}
        usePortal={true}
        portalContainer={document.body}
        className="review-dialog"
        style={{ width: 520, paddingBottom: 10, borderRadius: "12px", zIndex: 9002 }}
      >
        <div className="bp4-dialog-body">

          <label className="dialog-label">Step Name</label>

          <FormGroup
            helperText={errors.stepName}
            intent={errors.stepName ? "danger" : "none"}
            style={{ flexGrow: 1, marginBottom: 0 }}
          >
            <input
              type="text"
              className="bp3-input"
              placeholder="e.g., Team Lead Review"
              value={editData.stepName}
              onChange={(e) => {
                const value = e.target.value;
                const words = value.trim().split(/\s+/).filter(Boolean);
                if (words.length <= MAX_WORDS) {
                  updateStepField("stepName", value);
                  if (errors.stepName) {
                    setErrors((prev) => ({ ...prev, stepName: "" }));
                  }
                } else {
                  setErrors((prev) => ({
                    ...prev,
                    stepName: `Step name cannot exceed ${MAX_WORDS} words.`,
                  }));
                }
              }}
              style={{
                height: "38px",
                width: "100%",
                border: "1px solid #dedede",
                boxShadow: "none"
              }}
            />
          </FormGroup>


          {/* SCROLLABLE DROPDOWN */}
          <label className="dialog-label">Reviewer</label>

          <FormGroup
            helperText={errors.reviewer}
            intent={errors.reviewer ? "danger" : "none"}
          >
            <div className="custom-reviewer-dropdown" style={{ width: "100%", maxWidth: 460 }}>
              <ScrollableDropdown
                width={460}
                label={
                  filteredEmployeeList.length === 0
                    ? "No employees for this job profile"
                    : selectedReviewer
                      ? `${selectedReviewer.first_name} ${selectedReviewer.last_name || ""}`
                      : "Select Reviewer"
                }
                items={filteredEmployeeList}
                getKey={(e) => e.id}
                getLabel={(e) => `${e.first_name} ${e.last_name || ""}`}
                value={editData.reviewerName}
                onSelect={(employee) => {
                  handleSelectReviewer(employee);
                  if (errors.reviewer) setErrors(prev => ({ ...prev, reviewer: "" }));
                }}
                enableSearch
                onSearch={handleSearchEmployees}
              />
            </div>
          </FormGroup>

          {Number(ratingMethod) === 2 && (
            <>
              <label className="dialog-label">Weightage</label>
              <FormGroup helperText={errors.weightage} intent={errors.weightage ? "danger" : "none"}>
                <input
                  type="number"
                  min="0"
                  max="100"
                  className="bp3-input"
                  value={editData.weightage}
                  onChange={(e) => {
                    updateStepField("weightage", e.target.value);
                    if (errors.weightage) setErrors(prev => ({ ...prev, weightage: "" }));
                  }}
                  style={{ height: "38px", width: "100%", border: "1px solid #dedede", boxShadow: "none" }}
                  placeholder="Enter weight (e.g., 30)"
                />
              </FormGroup>
            </>
          )}

          <div className="dialog-footer">
            {canModify && (
              <FilledButton text={editIndex === null ? "+ Add Step" : "Save"} onClick={saveStep} />)}
            {editIndex !== null && (
              <span
                onClick={deleteStep}
                style={{
                  color: "#1870d5",
                  cursor: "pointer",
                  fontSize: "14px",
                  marginTop: "7px",
                }}
              >
                Delete Step
              </span>
            )}
          </div>


        </div>
      </Dialog >
    </>
  );
}